import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageSquare, Send, Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface QuestionOption {
  id: string;
  value: string;
  text: string;
}

interface Question {
  id: string;
  type: 'OPEN_TEXT' | 'MULTIPLE_CHOICE' | 'RATING' | 'YES_NO';
  text: string;
  isRequired: boolean;
  order: number;
  options: QuestionOption[];
}

interface CustomForm {
  id: string;
  name: string;
  product: {
    id: string;
    name: string;
  };
  questions: Question[];
}

interface CustomFormRendererProps {
  form: CustomForm;
  jwtToken?: string;
  onSubmissionSuccess: (responseId: string) => void;
}

interface FormAnswer {
  questionId: string;
  value: string;
}

const CustomFormRenderer = ({ form, jwtToken, onSubmissionSuccess }: CustomFormRendererProps) => {
  const [answers, setAnswers] = useState<FormAnswer[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { toast } = useToast();

  const API_BASE_URL = import.meta.env.VITE_API_URL;

  const updateAnswer = (questionId: string, value: string) => {
    setAnswers(prev => {
      const existing = prev.find(a => a.questionId === questionId);
      if (existing) {
        return prev.map(a => a.questionId === questionId ? { ...a, value } : a);
      } else {
        return [...prev, { questionId, value }];
      }
    });
  };

  const validateForm = (): string[] => {
    const validationErrors: string[] = [];

    form.questions.forEach(question => {
      const answer = answers.find(a => a.questionId === question.id);
      
      if (question.isRequired) {
        if (!answer || !answer.value.trim()) {
          validationErrors.push(`"${question.text}" is required`);
          return;
        }
      }

      if (answer && answer.value.trim()) {
        switch (question.type) {
          case 'RATING':
            const rating = parseInt(answer.value);
            if (isNaN(rating) || rating < 1 || rating > 5) {
              validationErrors.push(`Rating for "${question.text}" must be between 1 and 5`);
            }
            break;
          
          case 'YES_NO':
            if (!['yes', 'no'].includes(answer.value.toLowerCase())) {
              validationErrors.push(`Answer for "${question.text}" must be "Yes" or "No"`);
            }
            break;
          
          case 'MULTIPLE_CHOICE':
            const validOption = question.options.find(opt => opt.value === answer.value);
            if (!validOption) {
              validationErrors.push(`Please select a valid option for "${question.text}"`);
            }
            break;
        }
      }
    });

    return validationErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const response = await fetch(`${API_BASE_URL}/custom-form-responses/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwtToken && { 'Authorization': `Bearer ${jwtToken}` }),
        },
        body: JSON.stringify({
          formId: form.id,
          productId: form.product.id,
          answers: answers.filter(a => a.value.trim()),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit form');
      }

      const result = await response.json();
      
      toast({
        title: "Success!",
        description: "Your form has been submitted successfully.",
      });

      onSubmissionSuccess(result.responseId);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "There was an error submitting your form. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const answer = answers.find(a => a.questionId === question.id);
    const hasError = errors.some(error => error.includes(question.text));

    switch (question.type) {
      case 'OPEN_TEXT':
        return (
          <div className="space-y-2">
            <Textarea
              placeholder="Enter your answer here..."
              value={answer?.value || ''}
              onChange={(e) => updateAnswer(question.id, e.target.value)}
              className={`min-h-24 resize-none ${hasError ? 'border-destructive' : ''}`}
            />
          </div>
        );

      case 'MULTIPLE_CHOICE':
        return (
          <RadioGroup
            value={answer?.value || ''}
            onValueChange={(value) => updateAnswer(question.id, value)}
            className={hasError ? 'border-destructive' : ''}
          >
            {question.options.map((option) => (
              <div key={option.id} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={option.id} />
                <Label htmlFor={option.id} className="text-sm font-normal">
                  {option.text}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'RATING':
        return (
          <div className="space-y-2">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <Button
                  key={rating}
                  type="button"
                  variant={answer?.value === rating.toString() ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateAnswer(question.id, rating.toString())}
                  className="w-12 h-12 p-0"
                >
                  <Star className={`w-5 h-5 ${answer?.value === rating.toString() ? 'fill-current' : ''}`} />
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {answer?.value ? `${answer.value} out of 5` : 'Click to rate'}
            </p>
          </div>
        );

      case 'YES_NO':
        return (
          <RadioGroup
            value={answer?.value || ''}
            onValueChange={(value) => updateAnswer(question.id, value)}
            className={hasError ? 'border-destructive' : ''}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id={`${question.id}-yes`} />
              <Label htmlFor={`${question.id}-yes`} className="text-sm font-normal">
                Yes
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id={`${question.id}-no`} />
              <Label htmlFor={`${question.id}-no`} className="text-sm font-normal">
                No
              </Label>
            </div>
          </RadioGroup>
        );

      default:
        return <div className="text-muted-foreground">Unknown question type</div>;
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels = {
      OPEN_TEXT: 'Open Text',
      MULTIPLE_CHOICE: 'Multiple Choice',
      RATING: 'Rating (1-5)',
      YES_NO: 'Yes/No',
    };
    return labels[type as keyof typeof labels] || type;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-2xl border-border/70 bg-card/90 backdrop-blur-sm">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-r from-primary via-brand-coral to-brand-amber rounded-full flex items-center justify-center">
            <MessageSquare className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-3xl font-bold text-primary">
            {form.name}
          </CardTitle>
          <CardDescription className="text-lg text-muted-foreground">
            {form.product.name}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-8">
            {form.questions.map((question, index) => (
              <div key={question.id} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-lg font-medium text-foreground">
                      {index + 1}. {question.text}
                    </Label>
                    {question.isRequired && (
                      <Badge variant="destructive" className="text-xs">
                        Required
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {getQuestionTypeLabel(question.type)}
                    </Badge>
                  </div>
                  
                  {renderQuestion(question)}
                  
                  {errors.some(error => error.includes(question.text)) && (
                    <p className="text-sm text-destructive">
                      {errors.find(error => error.includes(question.text))}
                    </p>
                  )}
                </div>
              </div>
            ))}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-gradient-to-r from-primary via-brand-coral to-brand-amber hover:from-primary/90 hover:via-brand-coral/90 hover:to-brand-amber/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-medium transition-all duration-200 transform hover:scale-[1.02]"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Submit Form
                </div>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomFormRenderer; 